import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@apollo/client';
import cx from 'classnames';
import styles from './VisitorWatchlistForm.scss';
import {
  TagInput,
  Button,
  Dropdown,
  Icon,
  Input,
  TextArea,
  Flex,
} from 'design-system-web';
import { Alert, AlertType } from 'components/lds';
import { useUserDataContext, useFlag } from 'lane-shared/hooks';
import { useChannelAdminContext } from 'hooks';
import { createVisitorWatchlistMutation, updateVisitorWatchlistMutation } from 'graphql-queries';
import { convertToUUID } from 'uuid-encoding';
import { AttachmentThumbnail, FileInput, RadioGroup, MultiselectField } from 'components';
import { getBuildingTenantChannels } from 'lane-shared/graphql/query';
import { CountryCode, parsePhoneNumber } from 'lane-shared/helpers/phoneNumbers';
import VisitorPhoneInput from '../VisitorPhoneInput/VisitorPhoneInput';
import VisitorPhoneTagInput from '../VisitorPhoneTagInput/VisitorPhoneTagInput';
import { capitalize } from 'formatters';
import { FeatureFlag } from 'constants-flags';
import {
  VisitorWatchlistParams,
  NameInputFieldsProps,
  ContactInputFieldsProps,
  WATCHLIST_LEVELS,
  WatchlistRestrictAtLevel,
  createVisitorWatchlistInput,
  updateVisitorWatchlistInput,
  getInitialVisitorWatchlistParams,
  mapVisitorWatchlistToParams,
} from '../../types/VisitorWatchlist';
import { WatchlistLevelSelector } from '../WatchlistLevelSelector';
import type { WatchlistSeverityLevel, VisitorWatchlist } from 'graphql-query-contracts';
import { validateEmail } from '../../validators/emailValidator';
import {
  AttachmentResponse,
  EntityTypeEnum,
} from 'lane-shared/types/attachment';

import { FileReturnTypeEnum } from 'helpers/fileReaderResolver';
import { useAttachmentUpload } from 'hooks/useAttachmentUpload';
import { useAttachmentByEntityData } from 'hooks/useAttachmentByEntityData';
import { useAttachmentDelete } from 'hooks/useAttachmentDelete';
import { AmazonS3Buckets, routes } from 'lane-shared/config';
import React, { useState, useEffect } from 'react';
import { TFunction } from 'i18next';
import { useHistory } from 'react-router-dom';
import useIsAdminView from 'hooks/useIsAdminView';

const NameInputFields = ({
  visitorWatchlistParams,
  setVisitorWatchlistParams,
  showFirstNameAliases,
  setShowFirstNameAliases,
  showLastNameAliases,
  setShowLastNameAliases,
  t,
}: NameInputFieldsProps) => (
  <Flex gap={4} align="flex-start">
    <Flex direction="column" gap={4} align="flex-start" style={{ flex: 1 }}>
      <Input
        value={capitalize(visitorWatchlistParams.firstname)}
        label={t('web.admin.channel.visitor.watchlist.firstName')}
        placeholder={t(
          'web.admin.channel.visitor.watchlist.firstNamePlaceholder'
        )}
        fixedLabel
        isRequired
        onChange={(value: string) =>
          setVisitorWatchlistParams({
            ...visitorWatchlistParams,
            firstname: value,
          })
        }
      />
      {!showFirstNameAliases && (
        <Button
          variant="secondary"
          onClick={() => setShowFirstNameAliases(true)}
          startIcon={<Icon name="plus" />}
        >
          {t('web.admin.channel.visitor.watchlist.addAliases')}
        </Button>
      )}
    </Flex>

    <Flex direction="column" gap={4} align="flex-start" style={{ flex: 1 }}>
      <Input
        value={capitalize(visitorWatchlistParams.lastname)}
        label={t('web.admin.channel.visitor.watchlist.lastName')}
        placeholder={t(
          'web.admin.channel.visitor.watchlist.lastNamePlaceholder'
        )}

        fixedLabel
        isRequired
        onChange={(value: string) =>
          setVisitorWatchlistParams({
            ...visitorWatchlistParams,
            lastname: value,
          })
        }
      />
      {!showLastNameAliases && (
        <Button
          variant="secondary"
          onClick={() => setShowLastNameAliases(true)}
          startIcon={<Icon name="plus" />}
        >
          {t('web.admin.channel.visitor.watchlist.addAliases')}
        </Button>
      )}
    </Flex>
  </Flex>
);

const ContactInputFields = ({
  visitorWatchlistParams,
  setVisitorWatchlistParams,
  showAdditionalEmails,
  setShowAdditionalEmails,
  setPrimaryPhoneCountryCode,
  primaryPhoneCountryCode,
  setAlternatePhoneCountryCode,
  showAdditionalPhone,
  setShowAdditionalPhone,
  emailError,
  handleEmailChange,
  t,
  styles,
}: ContactInputFieldsProps & { 
  setPrimaryPhoneCountryCode: (code: CountryCode) => void;
  primaryPhoneCountryCode: CountryCode;
  setAlternatePhoneCountryCode: (code: CountryCode) => void;
  showAdditionalPhone: boolean;
  setShowAdditionalPhone: (show: boolean) => void;
  styles: Record<string, string> 
}) => (
  <Flex direction="column" gap={4} align="flex-start">
    <Flex gap={4} align="flex-end" style={{ width: '100%' }}>
      <Input
        value={visitorWatchlistParams.primaryEmail}
        label={t('web.admin.channel.visitor.watchlist.primaryEmail')}
        placeholder={t('web.admin.channel.visitor.watchlist.emailPlaceholder')}
        type="email"
        error={emailError ? [emailError] : undefined}
        fixedLabel
        onChange={handleEmailChange}
        style={{ flex: 1 }}
      />
      {!showAdditionalEmails && (
        <Button
          variant="secondary"
          onClick={() => setShowAdditionalEmails(true)}
          startIcon={<Icon name="plus" />}
        >
          {t('web.admin.channel.visitor.watchlist.addAnotherEmail')}
        </Button>
      )}
    </Flex>

    {showAdditionalEmails && (
      <Flex direction="column" style={{ width: '100%' }}>
        <label className={styles.fieldLabel}>
          {t('web.admin.channel.visitor.watchlist.additionalEmails')}
        </label>
        <TagInput
          placeholder={t(
            'web.admin.channel.visitor.watchlist.enterAlternativeEmails'
          )}
          tags={visitorWatchlistParams.additionalEmails}
          validateTag={validateEmail}
          setTags={(tags: string[]) =>
            setVisitorWatchlistParams({
              ...visitorWatchlistParams,
              additionalEmails: tags,
            })
          }
        />
      </Flex>
    )}

    <Flex direction="row" gap={4} align="flex-end" style={{ width: '100%' }}>
      <div style={{ flex: 1 }}>
        <VisitorPhoneInput
          className={styles.primaryPhoneInput}
          value={visitorWatchlistParams.primaryPhone}
          displayName={t('web.admin.channel.visitor.watchlist.primaryPhone')}
          placeholder={t('web.admin.channel.visitor.watchlist.phoneNumberPlaceholder')}
          onChange={(value: string) => {
            setVisitorWatchlistParams({
              ...visitorWatchlistParams,
              primaryPhone: value,
            });
          }}
          onRegionChange={(regionCode: CountryCode) => {
            setPrimaryPhoneCountryCode(regionCode);
          }}
        />
      </div>
      {!showAdditionalPhone && (
        <Button
          variant="secondary"
          onClick={() => {
            setShowAdditionalPhone(true);
            setAlternatePhoneCountryCode(primaryPhoneCountryCode);
          }}
          startIcon={<Icon name="plus" />}
          style={{ flexShrink: 0 }}
        >
          {t('web.admin.channel.visitor.watchlist.addAnotherPhone')}
        </Button>
      )}
    </Flex>
  </Flex>
);

const AliasFields = ({
  visitorWatchlistParams,
  setVisitorWatchlistParams,
  showFirstNameAliases,
  showLastNameAliases,
  t,
  styles,
}: {
  visitorWatchlistParams: VisitorWatchlistParams;
  setVisitorWatchlistParams: (params: VisitorWatchlistParams) => void;
  showFirstNameAliases: boolean;
  showLastNameAliases: boolean;
  t: TFunction<'translation', undefined>;
  styles: Record<string, string>;
}) => {
  // Only show container when aliases are displayed
  const hasAliases = showFirstNameAliases || showLastNameAliases;
  
  if (!hasAliases) {
    return null;
  }

  return (
    <Flex direction="column" gap={4} align="flex-start">
      {showFirstNameAliases && (
        <Flex direction="column">
          <label className={styles.fieldLabel}>
            {t('web.admin.channel.visitor.watchlist.additionalFirstNames')}
          </label>
          <TagInput
            placeholder={t(
              'web.admin.channel.visitor.watchlist.enterAlternativeNames'
            )}
            tags={visitorWatchlistParams.firstnameAliases.map(capitalize)}
            setTags={(tags: string[]) =>
              setVisitorWatchlistParams({
                ...visitorWatchlistParams,
                firstnameAliases: tags,
              })
            }
          />
        </Flex>
      )}

      {showLastNameAliases && (
        <Flex direction="column">
          <label className={styles.fieldLabel}>
            {t('web.admin.channel.visitor.watchlist.additionalLastNames')}
          </label>
          <TagInput
            placeholder={t(
              'web.admin.channel.visitor.watchlist.enterAlternativeLastNames'
            )}
            tags={visitorWatchlistParams.lastnameAliases.map(capitalize)}
            setTags={(tags: string[]) =>
              setVisitorWatchlistParams({
                ...visitorWatchlistParams,
                lastnameAliases: tags,
              })
            }
          />
        </Flex>
      )}
    </Flex>
  );
};

const AdditionalContactFields = ({
  visitorWatchlistParams,
  setVisitorWatchlistParams,
  alternatePhoneCountryCode,
  setAlternatePhoneCountryCode,
  showAdditionalPhone,
  t,
  styles,
}: {
  visitorWatchlistParams: VisitorWatchlistParams;
  setVisitorWatchlistParams: (params: VisitorWatchlistParams) => void;
  alternatePhoneCountryCode: CountryCode;
  setAlternatePhoneCountryCode: (code: CountryCode) => void;
  showAdditionalPhone: boolean;
  t: TFunction<'translation', undefined>;
  styles: Record<string, string>;
}) => {
  // Only show if additional phone is enabled
  if (!showAdditionalPhone) {
    return null;
  }

  return (
    <div style={{ width: '100%' }}>
        <VisitorPhoneTagInput
          className={styles.alternatePhoneInput}
          displayName={t('web.admin.channel.visitor.watchlist.additionalPhones')}
          placeholder={t('web.admin.channel.visitor.watchlist.phoneNumberPlaceholder')}
          tags={visitorWatchlistParams.additionalPhones}
          setTags={(tags: string[]) => {
            setVisitorWatchlistParams({
              ...visitorWatchlistParams,
              additionalPhones: tags,
            });
          }}
          onRegionChange={(regionCode: CountryCode) => {
            setAlternatePhoneCountryCode(regionCode);
          }}
          errorText={t('web.admin.channel.visitor.watchlist.error.invalidPhone')}
          regionCode={alternatePhoneCountryCode}
        />
    </div>
  );
};

interface VisitorWatchlistFormProps {
  className?: string;
  isEditMode?: boolean;
  watchlist?: VisitorWatchlist;
  onEditSuccess?: () => void;
}


function VisitorWatchlistForm({ 
  className, 
  isEditMode = false, 
  watchlist, 
  onEditSuccess 
}: VisitorWatchlistFormProps) {
  const { t } = useTranslation();
  const [createVisitorWatchlist, { loading: createLoading }] = useMutation(
    createVisitorWatchlistMutation
  );
  const [updateVisitorWatchlist, { loading: updateLoading }] = useMutation(
    updateVisitorWatchlistMutation
  );
  const [attachmentFiles, setAttachmentFiles] = useState<AttachmentResponse[]>(
    []
  );
  const [existingAttachments, setExistingAttachments] = useState<AttachmentResponse[]>(
    []
  );
  const { user } = useUserDataContext();
  const { channel } = useChannelAdminContext();
  const history = useHistory();
  const [, channelSlug] = useIsAdminView();
  const isWatchlistV2Enabled = useFlag(
    FeatureFlag.VisitorManagementWatchlistV2,
    false
  );

  // Load existing attachments when in edit mode
  const { attachments: loadedAttachments, fetchAttachments } = useAttachmentByEntityData({
    entityId: watchlist?.id || '',
    editMode: false,
  });

  // Update existing attachments when loaded
  useEffect(() => {
    if (isEditMode && loadedAttachments.length > 0) {
      setExistingAttachments(loadedAttachments);
    }
  }, [isEditMode, loadedAttachments]);

  const [visitorWatchlistParams, setVisitorWatchlistParams] =
    useState<VisitorWatchlistParams>(() => {
      if (isEditMode && watchlist) {
        return mapVisitorWatchlistToParams(watchlist, isWatchlistV2Enabled);
      }

      return getInitialVisitorWatchlistParams();
    });

/**
 * Track the selected restriction level - maintain state separately from tenantIds
 * This is used to determine the UI to show the user
 */
  const [restrictAt, setRestrictAt] = useState<WatchlistRestrictAtLevel>(() => {
    if (isEditMode && watchlist?.tenantIds && watchlist.tenantIds.length > 0) {
      return WatchlistRestrictAtLevel.TenantLevel;
    }

    return WatchlistRestrictAtLevel.PropertyLevel;
  });

  useEffect(() => {
    if (visitorWatchlistParams.tenantIds && visitorWatchlistParams.tenantIds.length > 0) {
      setRestrictAt(WatchlistRestrictAtLevel.TenantLevel);
    }
  }, [visitorWatchlistParams.tenantIds]);

  const [showFirstNameAliases, setShowFirstNameAliases] = useState(() => {
    if (isEditMode && watchlist) {
      return (watchlist.visitorMatchingInfo?.firstName?.alternateValues?.length || 0) > 0;
    }

    return false;
  });

  const [showLastNameAliases, setShowLastNameAliases] = useState(() => {
    if (isEditMode && watchlist) {
      return (watchlist.visitorMatchingInfo?.lastName?.alternateValues?.length || 0) > 0;
    }

    return false;
  });

  const [showAdditionalEmails, setShowAdditionalEmails] = useState(() => {
    if (isEditMode && watchlist) {
      return (watchlist.visitorMatchingInfo?.email?.alternateValues?.length || 0) > 0;
    }

    return false;
  });

  const [showAdditionalPhone, setShowAdditionalPhone] = useState<boolean>(() => {
    if (isEditMode && watchlist) {
      const alternatePhones = watchlist.visitorMatchingInfo?.phone?.alternateValues || [];

      return Array.isArray(alternatePhones) && alternatePhones.length > 0;
    }

    return false;
  });
  const [primaryPhoneCountryCode, setPrimaryPhoneCountryCode] = useState<CountryCode>(() => {
    if (isEditMode && watchlist?.visitorMatchingInfo?.phone?.primaryValue) {
      try {
        const parsed = parsePhoneNumber(watchlist.visitorMatchingInfo.phone.primaryValue);

        return parsed?.country || 'US';
      } catch {
        return 'US';
      }
    }

    return 'US';
  });

  const [alternatePhoneCountryCode, setAlternatePhoneCountryCode] = useState<CountryCode>(() => {
    if (isEditMode && watchlist?.visitorMatchingInfo?.phone?.alternateValues?.length) {
      // Try to parse each alternate phone number to find one with a valid country code
      for (const phoneNumber of watchlist.visitorMatchingInfo.phone.alternateValues) {
        try {
          const parsed = parsePhoneNumber(phoneNumber);

          if (parsed?.country) {
            return parsed.country;
          }
        } catch {
          // Continue to next phone number
        }
      }
    }

    return 'US';
  });
  const isLoading = createLoading || updateLoading;
  const [error, setError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);

  const resetForm = () => {
    const initialParams = getInitialVisitorWatchlistParams();

    setVisitorWatchlistParams(initialParams);
    setShowFirstNameAliases(false);
    setShowLastNameAliases(false);
    setShowAdditionalEmails(false);
    setShowAdditionalPhone(false);
    setEmailError(null);
    setError(null);
    setAttachmentFiles([]);
    setExistingAttachments([]);
    setPrimaryPhoneCountryCode('US');
    setAlternatePhoneCountryCode('US');
    setRestrictAt(WatchlistRestrictAtLevel.PropertyLevel);
  };

  // Fetch tenants for multi-select dropdown
  const {
    data: buildingTenants,
  }: {
    data?: { getBuildingTenantChannels: Array<{ channelId: string; name: string }> };
  } = useQuery(getBuildingTenantChannels, {
    variables: { channelId: channel?._id ? convertToUUID(channel._id) : '' },
    skip: !channel?._id,
  });

  // Prepare tenant options for multi-select
  const tenantOptions = buildingTenants?.getBuildingTenantChannels?.map(tenant => ({
    value: convertToUUID(tenant.channelId),
    label: tenant.name || '',
  })) || [];

  const addAttachment = async (files: any) => {
    const attachmentsToAdd = files.map((file: any) => ({
      file,
      thumbnailSignedUrl: URL.createObjectURL(file),
    }));
    const newAttachmentFiles = [...attachmentFiles, ...attachmentsToAdd];

    setAttachmentFiles(newAttachmentFiles);
  };

  const deleteAttachment = async (index: string) => {
    const newAttachmentFiles = [...attachmentFiles];

    newAttachmentFiles.splice(Number(index), 1);
    setAttachmentFiles(newAttachmentFiles);
  };

  const { uploading: _, filesSelectedHandler } = useAttachmentUpload({
    onAttachmentCreated: () => null,
    entity: {
      type: EntityTypeEnum.VisitorAttachment,
      id: watchlist?.id || '',
    },
    s3Bucket: AmazonS3Buckets.Activate,
  });

  const { deleteAttachmentHandler } = useAttachmentDelete({
    fetchAttachments,
  });

  const saveAttachments = async (watchlistId: string) => {
    try {
      // Save new attachments
      if (attachmentFiles.length > 0) {
        await filesSelectedHandler(
          attachmentFiles.map(attachment => attachment.file as File),
          watchlistId
        );
      }
    } catch { 
      // no need to handle error as we are not showing any error message 
      // & not throwing any error
      // allowing the user to save the watchlist without attachments
    }
  };

  const handleEmailValidation = async (email: string) => {
    if (!email) {
      setEmailError(null);

      return;
    }
  

    const isValid = validateEmail(email);

    if (!isValid) {
      setEmailError(
        t('web.admin.channel.visitor.watchlist.error.invalidEmail')
      );
    } else {
      setEmailError(null);
    }
  };

  const handleEmailChange = (value: string) => {
    setVisitorWatchlistParams({
      ...visitorWatchlistParams,
      primaryEmail: value,
    });
    handleEmailValidation(value);
  };

  const areRequiredFieldsFilled = () => {
    return (
      visitorWatchlistParams.firstname.trim() !== '' &&
      visitorWatchlistParams.lastname.trim() !== '' &&
      !emailError
    );
  };

  const handleUpdateSuccess = (data: any) => {
    const { firstName, lastName } = data.updateVisitorWatchlist.visitorWatchlist.visitorMatchingInfo;

    window.Toast.show(
      t('web.admin.channel.visitor.watchlist.updated.success', {
        name: `${firstName?.primaryValue} ${lastName?.primaryValue}`,
      })
    );

    if (onEditSuccess) {
      onEditSuccess();
    } else if (channelSlug) {
      history.push(
        routes.visitorManagementWatchlist.replace(':id', channelSlug)
      );
    }
  };

  const handleCreateSuccess = (data: any) => {
    const { firstName, lastName } = data.createVisitorWatchlist.visitorWatchlist.visitorMatchingInfo;

    window.Toast.show(
      t('web.admin.channel.visitor.watchlist.created.success', {
        name: `${firstName?.primaryValue} ${lastName?.primaryValue}`,
      })
    );

    resetForm();
    setError(null);

    if (channelSlug) {
      history.push(
        routes.visitorManagementWatchlist.replace(':id', channelSlug)
      );
    }
  };

  const handleUpdate = async (organizationId: string) => {
    if (!watchlist?.id || !user?._id) {
      throw new Error('Watchlist ID and user ID are required for update');
    }

    const input = updateVisitorWatchlistInput(
      visitorWatchlistParams,
      watchlist.id,
      organizationId,
      user._id,
      isWatchlistV2Enabled
    );

    const { data } = await updateVisitorWatchlist({
      variables: { input },
    });

    if (attachmentFiles.length > 0 && data?.updateVisitorWatchlist?.visitorWatchlist?.id) {
      await saveAttachments(data.updateVisitorWatchlist.visitorWatchlist.id);
    }

    if (data?.updateVisitorWatchlist?.visitorWatchlist) {
      handleUpdateSuccess(data);
    }
  };

  const handleCreate = async (organizationId: string) => {
    if (!user?._id) {
      throw new Error('User ID is required for create');
    }

    const input = createVisitorWatchlistInput(
      visitorWatchlistParams,
      organizationId,
      user._id,
      isWatchlistV2Enabled
    );

    const { data } = await createVisitorWatchlist({
      variables: { input },
    });

    if (attachmentFiles.length > 0 && data?.createVisitorWatchlist?.visitorWatchlist?.id) {
      await saveAttachments(data.createVisitorWatchlist.visitorWatchlist.id);
    }

    if (data?.createVisitorWatchlist?.visitorWatchlist) {
      handleCreateSuccess(data);
    }
  };

  const handleSave = async () => {
    setError(null);

    if (!user?._id) {
      setError(t('web.admin.channel.visitor.watchlist.error.userNotFound'));

      return;
    }

    if (!channel?._id) {
      setError(t('web.admin.channel.visitor.watchlist.error.channelNotFound'));

      return;
    }

    try {
      const organizationId = convertToUUID(channel._id);

      if (isEditMode) {
        await handleUpdate(organizationId);
      } else {
        await handleCreate(organizationId);
      }
    } catch (err: unknown) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} visitor watchlist:`, err);
      const errorMessage = err instanceof Error ? err.message : t(`web.admin.channel.visitor.watchlist.${isEditMode ? 'updated' : 'created'}.error`);

      setError(errorMessage);
    }
  };

  const handleCancel = () => {
    resetForm();
    history.goBack();
  };

  const restrictAtOptions = [
    {
      id: WatchlistRestrictAtLevel.PropertyLevel,
      text: t('web.admin.channel.visitor.watchlist.restrictAt.propertyLevel'),
    },
    {
      id: WatchlistRestrictAtLevel.TenantLevel,
      text: t('web.admin.channel.visitor.watchlist.restrictAt.tenantLevel'),
    },
  ];

  return (
    <Flex
      className={cx(styles.visitorWatchlistForm, className)}
      direction="column"
      gap={4}
    >
      {error && (
        <Alert type={AlertType.error} fullWidth>
          {error}
        </Alert>
      )}

      <NameInputFields
        visitorWatchlistParams={visitorWatchlistParams}
        setVisitorWatchlistParams={setVisitorWatchlistParams}
        showFirstNameAliases={showFirstNameAliases}
        setShowFirstNameAliases={setShowFirstNameAliases}
        showLastNameAliases={showLastNameAliases}
        setShowLastNameAliases={setShowLastNameAliases}
        t={t}
      />

      <AliasFields
        visitorWatchlistParams={visitorWatchlistParams}
        setVisitorWatchlistParams={setVisitorWatchlistParams}
        showFirstNameAliases={showFirstNameAliases}
        showLastNameAliases={showLastNameAliases}
        t={t}
        styles={styles}
      />

      <ContactInputFields
        visitorWatchlistParams={visitorWatchlistParams}
        setVisitorWatchlistParams={setVisitorWatchlistParams}
        showAdditionalEmails={showAdditionalEmails}
        setShowAdditionalEmails={setShowAdditionalEmails}
        setPrimaryPhoneCountryCode={setPrimaryPhoneCountryCode}
        primaryPhoneCountryCode={primaryPhoneCountryCode}
        setAlternatePhoneCountryCode={setAlternatePhoneCountryCode}
        showAdditionalPhone={showAdditionalPhone}
        setShowAdditionalPhone={setShowAdditionalPhone}
        emailError={emailError}
        handleEmailChange={handleEmailChange}
        t={t}
        styles={styles}
      />

      <AdditionalContactFields
        visitorWatchlistParams={visitorWatchlistParams}
        setVisitorWatchlistParams={setVisitorWatchlistParams}
        alternatePhoneCountryCode={alternatePhoneCountryCode}
        setAlternatePhoneCountryCode={setAlternatePhoneCountryCode}
        showAdditionalPhone={showAdditionalPhone}
        t={t}
        styles={styles}
      />

      <div className={styles.fieldGroup}>
        <Input
          value={visitorWatchlistParams.reportedBy || ''}
          label={t('web.admin.channel.visitor.watchlist.reportedBy')}
          placeholder={t(
            'web.admin.channel.visitor.watchlist.reportedByPlaceholder'
          )}
          helperText={t('web.admin.channel.visitor.watchlist.reportedByHelper')}
          fixedLabel
          onChange={(value: string) =>
            setVisitorWatchlistParams({
              ...visitorWatchlistParams,
              reportedBy: value,
            })
          }
        />
      </div>

      <div className={styles.fieldGroup}>
        <label className={styles.fieldLabel}>
          {t('web.admin.channel.visitor.watchlist.restrictAt')}
        </label>
        <RadioGroup
          items={restrictAtOptions}
          onChange={(value: string) => {
            const restrictAtValue = value as WatchlistRestrictAtLevel;
            setRestrictAt(restrictAtValue);
            setVisitorWatchlistParams({
              ...visitorWatchlistParams,
              tenantIds: restrictAtValue === WatchlistRestrictAtLevel.PropertyLevel ? [] : visitorWatchlistParams.tenantIds || [],
            });
          }}
          selected={restrictAt}
          name="restrictAt"
          showBorder
        />
        {restrictAt === WatchlistRestrictAtLevel.TenantLevel && (
          <div style={{ marginTop: '16px' }}>
            <MultiselectField
              items={tenantOptions}
              value={
                visitorWatchlistParams.tenantIds && visitorWatchlistParams.tenantIds.length > 0
                  ? tenantOptions.filter((option) =>
                      visitorWatchlistParams.tenantIds?.includes(option.value)
                    )
                  : []
              }
              onChange={(selectedValues: any[]) => {
                // Extract value property from objects and ensure they're in LongUUID format
                const tenantIds = selectedValues.map((item) => {
                  const value = typeof item === 'string' ? item : item.value;

                  // Convert to LongUUID format if not already
                  return convertToUUID(value);
                });

                setVisitorWatchlistParams({
                  ...visitorWatchlistParams,
                  tenantIds,
                });
              }}
              placeholder={t('web.admin.channel.visitor.watchlist.selectTenants')}
              isFullWidth
            />
            <p className={styles.fieldHelper}>
              {t('web.admin.channel.visitor.watchlist.restrictAt.tenantLevel.helper')}
            </p>
          </div>
        )}
      </div>

      {isWatchlistV2Enabled ? (
        <div className={cx(styles.fieldGroup, styles.watchlistLevelFieldGroup)}>
          <label className={styles.fieldLabel}>
            {t('web.admin.channel.visitor.watchlist.watchlistLevel')}{' '}
            <span className={styles.requiredAsterisk}>*</span>
          </label>
          <p className={styles.fieldHelper}>
            {t('web.admin.channel.visitor.watchlist.watchlistLevelSubtitle')}
          </p>
          {channel?._id && (
            <WatchlistLevelSelector
              channelId={channel._id}
              value={visitorWatchlistParams.watchlistLevel}
              selectedConfigId={visitorWatchlistParams.watchlistLevelConfigId}
              onChange={(level: WatchlistSeverityLevel, configId?: string) => {
                setVisitorWatchlistParams({
                  ...visitorWatchlistParams,
                  watchlistLevel: level,
                  watchlistLevelConfigId: configId,
                });
              }}
            />
          )}
        </div>
      ) : (
        <div className={styles.fieldGroup}>
        <div className={styles.watchlistLevelDropdown}>
          <Dropdown
            fixedLabel
            isRequired
            value={visitorWatchlistParams.watchlistLevel}
            label={t('web.admin.channel.visitor.watchlist.watchlistLevel')}
            testId="Watchlist level"
            onChange={item => {
              setVisitorWatchlistParams({
                ...visitorWatchlistParams,
                watchlistLevel: item.value,
              });
            }}
            items={WATCHLIST_LEVELS.map(level => ({
              value: level.value,
              label: t(level.label),
            }))}
          />
        </div>
        <p className={styles.fieldHelper}>
          {t('web.admin.channel.visitor.watchlist.watchlistLevelHelper')}
        </p>
      </div>
      )}

      <div className={styles.fieldGroup}>
        <TextArea
          value={visitorWatchlistParams.notes}
          label={t('web.admin.channel.visitor.watchlist.notes')}
          helperText={t('web.admin.channel.visitor.watchlist.notesPlaceholder')}
          testId="Notes"
          onChange={(value: string) =>
            setVisitorWatchlistParams({
              ...visitorWatchlistParams,
              notes: value,
            })
          }
          maxLength={500}
          minRows={4}
          showLengthIndicator
        />
      </div>

      <FileInput
        accept=".svg,.png,.jpg,.jpeg,.gif,.pdf"
        type={FileReturnTypeEnum.File}
        enableMultiUpload
        onFilesSelected={addAttachment}
      >
        <div>
          <label className={styles.fieldLabel}>
            {t('web.admin.channel.visitor.watchlist.attachments')}
          </label>
          <div className={styles.fileUpload}>
            <div className={styles.attachmentArea}>
              <Icon name="paperclip" className={styles.attachmentButtonIcon} />
            </div>
            <div className={styles.uploadArea}>
              <p className={styles.uploadInfoTitle}>{t('web.admin.channel.visitor.watchlist.clickToUpload')}</p>
              <p className={styles.uploadInfo}>
                {t('web.admin.channel.visitor.watchlist.uploadInfo.format')}
              </p>
              <p className={styles.uploadInfo}>
                {t('web.admin.channel.visitor.watchlist.uploadInfo.message')}
              </p>
            </div>
          </div>
        </div>
      </FileInput>
      <Flex direction="column" className={styles.attachments}>
        <Flex className={styles.attachmentThumbnail} gap={4}>
          {/* Display existing attachments */}
          {existingAttachments.map((attachment, index) => (
            <AttachmentThumbnail
              key={`existing-attachment-${attachment.id || index}`}
              attachment={attachment}
              deleteAttachmentHandler={async (id) => {
                try {
                  // Delete from server
                  if (deleteAttachmentHandler) {
                    await deleteAttachmentHandler(id);
                  }

                  // Remove from existing attachments
                  setExistingAttachments(prev => 
                    prev.filter(att => att.id !== id)
                  );
                } catch {
                  // no need to handle error as we are not showing any error message 
                  // & not throwing any error
                  // allowing the user to delete the attachment
                }
              }}
            />
          ))}
          {attachmentFiles.map((attachment, index) => (
            <AttachmentThumbnail
              key={`attachment-${index}`}
              attachment={{ ...attachment, id: String(index) }}
              deleteAttachmentHandler={deleteAttachment}
            />
          ))}
        </Flex>
      </Flex> 

      <Flex className={styles.actions} gap={4}>
        <Button 
          size="large"
          variant="primary" 
          onClick={handleSave} 
          disabled={isLoading || !areRequiredFieldsFilled()}
        >
          {isLoading
            ? t('web.admin.channel.visitor.watchlist.saving')
            : t('web.admin.channel.visitor.watchlist.save')}
        </Button>
        <Button size="large" variant="secondary" onClick={handleCancel} disabled={isLoading}>
          {t('web.admin.channel.visitor.watchlist.cancel')}
        </Button>
      </Flex>
    </Flex>
  );
}

export { VisitorWatchlistForm };
